import QuoteHelp from './QuoteHelp';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getLaborProfiles } from '../laborProfile.api';
import QuoteDecimalInput from './QuoteDecimalInput';

const baseDefinitions = [
  ['defaultVatRate', 'TVA par défaut', 'percentage', 'number'],
  ['laborCoefficient', 'Coefficient main-d’œuvre', 'number', 'number'],
  ['materialCoefficient', 'Coefficient matériel', 'number', 'number'],
  ['subcontractingCoefficient', 'Coefficient sous-traitance', 'number', 'number'],
  ['coContractingCoefficient', 'Coefficient co-traitance', 'number', 'number'],
  ['internalStudyCoefficient', 'Coefficient études internes', 'number', 'number'],
  ['externalStudyCoefficient', 'Coefficient études externes', 'number', 'number'],
  ['otherCoefficient', 'Coefficient autres coûts', 'number', 'number'],
  ['escalationFormula', 'Formule d’actualisation', 'formula', 'text'],
  ['revisionFormula', 'Formule de révision', 'formula', 'text'],
];

const valueOf = (parameter) => {
  if (['number', 'percentage'].includes(parameter.parameterType)) return parameter.valueNumber ?? '';
  if (parameter.parameterType === 'boolean') return Boolean(parameter.valueBoolean);
  if (parameter.parameterType === 'json') return JSON.stringify(parameter.valueJson ?? {});
  return parameter.valueText ?? parameter.valueDate ?? '';
};

const withValue = (parameter, value) => {
  const next = { ...parameter, sourceReference: null };
  if (['number', 'percentage'].includes(parameter.parameterType)) next.valueNumber = value;
  else if (parameter.parameterType === 'boolean') next.valueBoolean = value;
  else if (parameter.parameterType === 'date') next.valueDate = value;
  else if (parameter.parameterType === 'json') {
    try { next.valueJson = JSON.parse(value); } catch (_) { next.valueJson = value; }
  } else next.valueText = value;
  return next;
};

const ParameterOrigin = ({ parameter }) => (
  <span className={`quote-parameter-origin quote-parameter-origin--${parameter.sourceReference ? 'reference' : 'custom'}`}>
    {parameter.sourceReference ? 'Valeur du référentiel' : 'Personnalisée pour cette version'}
  </span>
);

const QuoteSettingsPanel = ({
  editor,
  localOnly = false,
  roundingModes,
  onUpdate,
  onUpdateParameters,
}) => {
  const parameters = useMemo(() => editor.versionParameters || [], [editor.versionParameters]);
  const byCode = useMemo(
    () => Object.fromEntries(parameters.map((parameter) => [parameter.parameterCode, parameter])),
    [parameters]
  );
  const labor = parameters.filter((parameter) => parameter.parameterCode.startsWith('laborRate.'));
  const [referenceLaborProfiles, setReferenceLaborProfiles] = useState([]);
  // This reference is loaded once per panel mount, using its initial mode.
  const initialLocalOnly = useRef(localOnly);
  useEffect(() => {
    if (initialLocalOnly.current) return undefined;
    let active = true;
    getLaborProfiles()
      .then((rows) => { if (active) setReferenceLaborProfiles(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (active) setReferenceLaborProfiles([]); });
    return () => { active = false; };
  }, []);
  const referenceLabor = referenceLaborProfiles.filter((profile) => profile.isActive !== false).map((profile, index) => {
    const today = new Date().toISOString().slice(0, 10);
    const rates = [...(profile.rates || [])].sort((a, b) => String(b.validFrom || '').localeCompare(String(a.validFrom || '')));
    const rate = rates.find((item) => (!item.validFrom || item.validFrom <= today) && (!item.validTo || item.validTo >= today)) || rates[0];
    return {
      parameterCode: `laborRate.${profile.code}`,
      parameterLabel: `Coût horaire - ${profile.label}`,
      parameterType: 'number',
      valueNumber: rate?.costHourlyRate ?? '',
      referenceValueNumber: rate?.costHourlyRate ?? '',
      sourceReference: rate?.id ? `labor_profile_rate:${rate.id}` : 'reference:labor-profile',
      referenceSourceReference: rate?.id ? `labor_profile_rate:${rate.id}` : 'reference:labor-profile',
      orderIndex: index,
    };
  });
  const laborByCode = Object.fromEntries(labor.map((parameter) => [parameter.parameterCode, parameter]));
  const referenceLaborCodes = new Set(referenceLabor.map((parameter) => parameter.parameterCode));
  const displayedLabor = [
    ...referenceLabor.map((reference) => ({
      ...reference,
      ...(laborByCode[reference.parameterCode] || {}),
      referenceValueNumber: reference.valueNumber,
      referenceSourceReference: reference.sourceReference,
    })),
    ...labor.filter((parameter) => !referenceLaborCodes.has(parameter.parameterCode)),
  ];
  const completeParametersWithLaborReference = () => {
    const existingCodes = new Set(parameters.map((parameter) => parameter.parameterCode));
    return [
      ...parameters,
      ...referenceLabor
        .filter((parameter) => !existingCodes.has(parameter.parameterCode))
        .map(({ referenceValueNumber, referenceSourceReference, ...parameter }) => parameter),
    ];
  };
  const knownCodes = new Set([...baseDefinitions.map(([code]) => code), 'roundingMode']);
  const custom = parameters.filter((parameter) =>
    !knownCodes.has(parameter.parameterCode) && !parameter.parameterCode.startsWith('laborRate.'));

  const replace = (parameter, value) => {
    const nextParameter = withValue(parameter, value);
    const completeParameters = completeParametersWithLaborReference();
    const exists = completeParameters.some((item) => item.parameterCode === parameter.parameterCode);
    onUpdateParameters(exists
      ? completeParameters.map((item) => item.parameterCode === parameter.parameterCode ? nextParameter : item)
      : [...completeParameters, { ...nextParameter, orderIndex: completeParameters.length }]);
    if (parameter.parameterCode === 'defaultVatRate') onUpdate('defaultVatRate', value);
    if (parameter.parameterCode === 'laborCoefficient') onUpdate('defaultMarginCoefficient', value);
  };

  const addCustom = () => {
    const suffix = Date.now();
    onUpdateParameters([...parameters, {
      parameterCode: `custom.${suffix}`,
      parameterLabel: 'Nouveau paramètre',
      parameterType: 'string',
      valueText: '',
      sourceReference: null,
      orderIndex: parameters.length,
    }]);
  };

  const resetLaborParameter = (parameter) => {
    if (parameter.referenceValueNumber === undefined) return;
    const completeParameters = completeParametersWithLaborReference();
    const restored = {
      ...parameter,
      valueNumber: parameter.referenceValueNumber,
      sourceReference: parameter.referenceSourceReference || 'reference:labor-profile',
    };
    delete restored.referenceValueNumber;
    delete restored.referenceSourceReference;
    onUpdateParameters(completeParameters.map((item) => item.parameterCode === restored.parameterCode ? restored : item));
  };

  const renderParameter = (parameter, inputType = 'text') => parameter && (
    <article className="quote-parameter-card" key={parameter.parameterCode}>
      <div><strong>{parameter.parameterLabel}</strong></div>
      <ParameterOrigin parameter={parameter} />
      {parameter.referenceValueNumber !== undefined && <small>Référentiel : {Number(parameter.referenceValueNumber || 0).toFixed(2).replace('.', ',')} €/h</small>}
      {inputType === 'number' ? <QuoteDecimalInput disabled={!editor.permissions?.canEdit} value={valueOf(parameter)} onChange={(value) => replace(parameter, value)} /> : <input disabled={!editor.permissions?.canEdit} type={inputType} value={valueOf(parameter)} onChange={(event) => replace(parameter, event.target.value)} />}
      {parameter.referenceValueNumber !== undefined && !parameter.sourceReference && <button type="button" className="basic-button" disabled={!editor.permissions?.canEdit} onClick={() => resetLaborParameter(parameter)}>Revenir au référentiel</button>}
    </article>
  );

  return (
    <section className={`quote-parameters ${!editor.permissions?.canEdit ? 'quote-panel--readonly' : ''}`}>
      <header><div><h2>Paramètres de la version <QuoteHelp title="Valeurs de référence">{`Les valeurs enregistrées ici restent attachées à V${editor.versionNumber}, même si le référentiel évolue.`}</QuoteHelp></h2></div><div className="quote-parameter-legend"><ParameterOrigin parameter={{ sourceReference: 'reference' }} /><ParameterOrigin parameter={{}} /></div></header>

      <div className="quote-parameter-group">
        <h3>Coûts horaires par métier</h3>
        <div className="quote-parameter-grid">{displayedLabor.length ? displayedLabor.map((parameter) => renderParameter(parameter, 'number')) : <p>Aucun profil tarifaire actif. Vérifiez le référentiel des profils métiers.</p>}</div>
      </div>

      <div className="quote-parameter-group">
        <h3>Coefficients par famille</h3>
        <div className="quote-parameter-grid">{baseDefinitions.slice(1, 8).map(([code, label, type, input]) => renderParameter(byCode[code] || { parameterCode: code, parameterLabel: label, parameterType: type, valueNumber: editor.defaultMarginCoefficient, sourceReference: 'reference:quote-defaults' }, input))}</div>
      </div>

      <div className="quote-parameter-group">
        <h3>Fiscalité et arrondis</h3>
        <div className="quote-parameter-grid">
          {renderParameter(byCode.defaultVatRate || { parameterCode: 'defaultVatRate', parameterLabel: 'TVA par défaut', parameterType: 'percentage', valueNumber: editor.defaultVatRate, sourceReference: 'reference:quote-defaults' }, 'number')}
          <article className="quote-parameter-card"><div><strong>Arrondi commercial</strong></div><ParameterOrigin parameter={byCode.roundingMode || { sourceReference: 'reference' }} /><select disabled={!editor.permissions?.canEdit} value={byCode.roundingMode?.valueText || editor.roundingMode} onChange={(event) => { const parameter = byCode.roundingMode || { parameterCode: 'roundingMode', parameterLabel: 'Arrondi commercial', parameterType: 'string' }; replace(parameter, event.target.value); onUpdate('roundingMode', event.target.value); }}>{!roundingModes.some((mode) => mode.value === (byCode.roundingMode?.valueText || editor.roundingMode)) && <option value={byCode.roundingMode?.valueText || editor.roundingMode} disabled>{byCode.roundingMode?.valueText || editor.roundingMode} (historique incompatible)</option>}{roundingModes.map((mode) => <option key={mode.value} value={mode.value} disabled={mode.disabled}>{mode.label}</option>)}</select></article>
        </div>
      </div>

      <div className="quote-parameter-group">
        <h3>Formules d’actualisation et de révision</h3>
        <div className="quote-parameter-grid">{baseDefinitions.slice(8).map(([code, label, type]) => renderParameter(byCode[code] || { parameterCode: code, parameterLabel: label, parameterType: type, valueText: '', sourceReference: 'reference:quote-defaults' }))}</div>
      </div>

      <div className="quote-parameter-group">
        <header><div><h3>Paramètres personnalisés <QuoteHelp title="Paramètres documentaires">Ces champs sont conservés avec la version mais n’altèrent pas les formules tant qu’ils ne correspondent pas à une cible économique ci-dessus.</QuoteHelp></h3></div><button className="basic-button active" type="button" disabled={!editor.permissions?.canEdit} onClick={addCustom}>Ajouter un paramètre</button></header>
        <div className="quote-parameter-grid">{custom.map((parameter) => (
          <article className="quote-parameter-card quote-parameter-card--custom" key={parameter.parameterCode}>
            <input disabled={!editor.permissions?.canEdit} value={parameter.parameterLabel} onChange={(event) => onUpdateParameters(parameters.map((item) => item === parameter ? { ...item, parameterLabel: event.target.value } : item))} />
            <ParameterOrigin parameter={parameter} />
            <input disabled={!editor.permissions?.canEdit} value={valueOf(parameter)} onChange={(event) => replace(parameter, event.target.value)} />
            <button type="button" disabled={!editor.permissions?.canEdit} onClick={() => onUpdateParameters(parameters.filter((item) => item !== parameter))}>Supprimer</button>
          </article>
        ))}</div>
      </div>
    </section>
  );
};

export default QuoteSettingsPanel;
